import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutline"
import CloseIcon from "@mui/icons-material/Close"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import FormControl from "@mui/material/FormControl"
import IconButton from "@mui/material/IconButton"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { capitalize } from "lodash"
import { createContext, useContext, useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import Column from "../../../../common/components/Column"

export default function MultiConditionEditor({
  conditionFieldsState, // type: ConditionFieldsStateWithOperator
  setConditionFieldsState,
  addConditionButtonTitle,
}) {
  const typedCandidateProperties = useContext(TypedCandidatePropertiesContext)
  const fieldOptions = Object.entries(typedCandidateProperties)
    .map((typedProperties) => typedProperties[1].fields)
    .reduce((comulatedFields, fields) => [...comulatedFields, ...fields], [])
    .map(getAutocompleteOption)
    .sort((op1, op2) => op1.label.localeCompare(op2.label))
  const fieldsState: ConditionFieldState =
    conditionFieldsState.conditionFieldsState

  function getSecondaryOptions(fieldValue) {
    let fieldType = null,
      comparisonOptions = [],
      targetOptions = []
    if (fieldValue) {
      for (const propertyType in typedCandidateProperties) {
        const property = typedCandidateProperties[propertyType]
        const field = property.fields.find(
          (field) => field.name === fieldValue.id
        )
        if (field) {
          fieldType = propertyType
          comparisonOptions = property.comparisons.map(getAutocompleteOption)
          targetOptions = field.choices
          break
        }
      }
    }
    return { fieldType, comparisonOptions, targetOptions }
  }

  function getAutocompleteOption(option) {
    return { ...option, id: option.name, label: option.display_name }
  }

  function handleFieldChanged(fieldIndex) {
    return (_, newValue) => {
      updateField(fieldIndex, {
        value: newValue,
        comparison: Option.getDefaultComparison(),
        comparisonTarget: "",
      })
    }
  }

  function handleFieldInputValueChanged(fieldIndex) {
    return (_, newValue) => {
      updateField(fieldIndex, { inputValue: newValue })
    }
  }

  function handleComparisonTargetChanged(fieldIndex) {
    return (e) =>
      updateField(fieldIndex, {
        comparisonTarget: e.target ? e.target.value : e,
      })
  }

  function handleComparisonChangedWrapper(fieldIndex, comparisonOptions) {
    return (e) => {
      updateField(fieldIndex, {
        comparison: comparisonOptions.find(
          (option) => option.id === e.target.value
        ),
      })
    }
  }

  function handleAddEntryConditionButtonClicked() {
    updateFieldsState(fieldsState.concat(new ConditionFieldState()))
  }

  function handleDeleteConditionIconButtonClicked(fieldIndex) {
    return () =>
      updateFieldsState(fieldsState.filter((_, index) => index !== fieldIndex))
  }

  function updateField(fieldIndex, fieldData) {
    updateFieldsState(
      fieldsState.map((fieldState, index) =>
        index === fieldIndex ? { ...fieldState, ...fieldData } : fieldState
      )
    )
  }

  function updateFieldsState(newFieldsState) {
    setConditionFieldsState(
      new ConditionFieldStateWithOperator(
        newFieldsState,
        conditionFieldsState.operator
      )
    )
  }

  return (
    <Column sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
      <Typography variant="body1" sx={{ mt: 1 }}>
        Candidate must match the following conditions
      </Typography>
      <ButtonGroup
        variant="outlined"
        color="inherit"
        size="small"
        sx={{ mt: 2 }}
      >
        {OPERATORS.map((operator, index) => (
          <Button
            key={index}
            className={
              operator === conditionFieldsState.operator ? "active" : undefined
            }
            onClick={() =>
              setConditionFieldsState({ ...conditionFieldsState, operator })
            }
          >
            {capitalize(operator)}
          </Button>
        ))}
      </ButtonGroup>
      <Column>
        {fieldsState.map((fieldState, fieldIndex) => {
          const { fieldType, comparisonOptions, targetOptions } =
            getSecondaryOptions(fieldState.value)
          return (
            <Grid container spacing={1} sx={{ mt: 3 }}>
              <Grid xs={6} sm={5}>
                <FieldNameAutocomplete
                  fieldIndex={fieldIndex}
                  fieldOptions={fieldOptions}
                  fieldState={fieldState}
                  handleFieldChanged={handleFieldChanged}
                  handleFieldInputValueChanged={handleFieldInputValueChanged}
                />
              </Grid>
              {fieldState.value && (
                <>
                  <Grid xs={6} sm={2}>
                    <ConditionSelect
                      comparisonOptions={comparisonOptions}
                      fieldIndex={fieldIndex}
                      fieldState={fieldState}
                      handleComparisonChangedWrapper={
                        handleComparisonChangedWrapper
                      }
                    />
                  </Grid>
                  <Grid xs={6} sm={4}>
                    <ValueEditor
                      fieldIndex={fieldIndex}
                      fieldState={fieldState}
                      fieldType={fieldType}
                      handleComparisonTargetChanged={
                        handleComparisonTargetChanged
                      }
                      targetOptions={targetOptions}
                    />
                  </Grid>
                  <Grid
                    xs={6}
                    sm={1}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <DeleteConditionIconButton
                      fieldIndex={fieldIndex}
                      fieldsState={fieldsState}
                      handleDeleteConditionIconButtonClicked={
                        handleDeleteConditionIconButtonClicked
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )
        })}
      </Column>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        startIcon={<AddCircleOutlinedIcon />}
        sx={{ mt: 3, alignSelf: "flex-start" }}
        onClick={handleAddEntryConditionButtonClicked}
      >
        {addConditionButtonTitle}
      </Button>
    </Column>
  )
}

function FieldNameAutocomplete({
  fieldState,
  handleFieldChanged,
  handleFieldInputValueChanged,
  fieldIndex,
  fieldOptions,
}) {
  return (
    <Autocomplete
      value={fieldState.value}
      onChange={handleFieldChanged(fieldIndex)}
      inputValue={fieldState.inputValue}
      onInputChange={handleFieldInputValueChanged(fieldIndex)}
      options={fieldOptions}
      renderInput={(props) => (
        <TextField label="Candidate property" {...props} />
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      size="small"
    />
  )
}

function ConditionSelect({
  fieldState,
  fieldIndex,
  comparisonOptions,
  handleComparisonChangedWrapper,
}) {
  return (
    <FormControl fullWidth>
      <InputLabel>Condition</InputLabel>
      <Select
        label="Condition"
        value={fieldState.comparison.id}
        onChange={handleComparisonChangedWrapper(fieldIndex, comparisonOptions)}
        size="small"
      >
        {comparisonOptions.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function ValueEditor({
  fieldType,
  // type: ConditionFieldState
  fieldState,
  fieldIndex,
  handleComparisonTargetChanged,
  targetOptions,
}) {
  return fieldType === FIELD_TYPES.string ? (
    targetOptions ? (
      <Select
        value={fieldState.comparisonTarget}
        onChange={handleComparisonTargetChanged(fieldIndex)}
        size="small"
        fullWidth
      >
        {Object.entries(targetOptions).map((option) => (
          <MenuItem key={option[0]} value={option[0]}>
            {option[1]}
          </MenuItem>
        ))}
      </Select>
    ) : (
      <TextField
        value={fieldState.comparisonTarget}
        onChange={handleComparisonTargetChanged(fieldIndex)}
        size="small"
      />
    )
  ) : fieldType === FIELD_TYPES.number ? (
    <TextField
      value={fieldState.comparisonTarget}
      onChange={handleComparisonTargetChanged(fieldIndex)}
      type="number"
      size="small"
    />
  ) : fieldType === FIELD_TYPES.date ? (
    <DateTimePicker
      value={new Date(fieldState.comparisonTarget)}
      onChange={handleComparisonTargetChanged(fieldIndex)}
      size="small"
      sx={{ "& .MuiInputBase-input": { py: 1 } }}
    />
  ) : (
    <Select
      value={fieldState.comparisonTarget}
      onChange={handleComparisonTargetChanged(fieldIndex)}
      size="small"
      fullWidth
    >
      {targetOptions.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </Select>
  )
}

function DeleteConditionIconButton({
  fieldsState,
  fieldIndex,
  handleDeleteConditionIconButtonClicked,
}) {
  return fieldsState.length === 1 ? (
    <></>
  ) : (
    <IconButton
      size="small"
      color="error"
      onClick={handleDeleteConditionIconButtonClicked(fieldIndex)}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  )
}

const TypedField = { fields: [], comparisons: [] }

const EMPTY_CTP_RESPONSE = {
  str: TypedField,
  int: TypedField,
  datetime: TypedField,
  ForeignKey: TypedField,
}

export function useLoadCandidateTypedProperties() {
  // state resets if used directly inside MultiConditionEditor after drawer is closed and reopened
  const [typedCandidateProperties, setTypedCandidateProperties] =
    useState(EMPTY_CTP_RESPONSE)
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      axios
        .get("/workflows/api/workflows/typed-candidate-properties/")
        .then(({ data }) => setTypedCandidateProperties(data))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return {
    typedCandidateProperties,
  }
}

export const TypedCandidatePropertiesContext = createContext(EMPTY_CTP_RESPONSE)

const COMPARISON_SYMBOL_MAP = {
  equals: "=",
  not_equals: "!=",
  contains: "~",
  not_contains: "!~",
  starts_with: "~-",
  ends_with: "-~",
  less_than: "<",
  greater_than: ">",
  before: "<",
  after: ">",
}

const FIELD_TYPES = {
  string: "str",
  number: "int",
  date: "datetime",
  foreignKey: "ForeignKey",
}

const OPERATORS = ["all", "any", "none"]

export class ConditionFieldStateWithOperator {
  constructor(
    conditionFieldsState: Array<ConditionFieldState>,
    operator: string
  ) {
    this.conditionFieldsState = conditionFieldsState
    this.operator = operator
  }
  static getDefaultState() {
    return new ConditionFieldStateWithOperator(
      ConditionFieldState.getDefaultFieldsState(),
      OPERATORS[0]
    )
  }
}

export class ConditionFieldState {
  constructor(
    inputValue = "",
    value = null,
    comparison = null,
    comparisonTarget = ""
  ) {
    this.inputValue = inputValue
    this.value = value
    let finalComparison = comparison
    if (!comparison) {
      finalComparison = Option.getDefaultComparison()
    }
    this.comparison = finalComparison
    this.comparisonTarget = comparisonTarget
  }
  static getDefaultFieldsState() {
    return [new ConditionFieldState()]
  }
}

class Option {
  constructor(name, displayName, id, label) {
    this.name = name
    this.display_name = displayName
    this.id = id
    this.label = label
  }
  static getDefaultComparison() {
    return new Option(
      "equals",
      "Equals",
      "equals",
      COMPARISON_SYMBOL_MAP.equals
    )
  }
}
